import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Users, Plus, Edit, Trash2, Search, Loader2, GripVertical,
  Clock, DollarSign, Save, ChevronDown, ChevronUp, Settings,
  FileText, Target, Briefcase, Award, MessageSquare, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import api from '@/lib/api';

const iconOptions = [
  'FileText', 'Target', 'Briefcase', 'Award', 'MessageSquare', 
  'TrendingUp', 'Users', 'Clock', 'DollarSign', 'Settings'
];

const categoryOptions = [
  { id: 'general', label: 'General' },
  { id: 'job-search', label: 'Job Search' },
  { id: 'professional-skills', label: 'Professional Skills' },
  { id: 'networking', label: 'Networking' },
  { id: 'career-advancement', label: 'Career Advancement' },
  { id: 'leadership', label: 'Leadership' },
  { id: 'corporate', label: 'Corporate Training' },
  { id: 'education', label: 'Education' },
  { id: 'career-exploration', label: 'Career Exploration' },
];

const AdminWorkshops = () => {
  const [activeTab, setActiveTab] = useState('topics');
  const [workshops, setWorkshops] = useState([]);
  const [pricingMatrix, setPricingMatrix] = useState({ durations: [], groupSizes: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [isWorkshopDialogOpen, setIsWorkshopDialogOpen] = useState(false);
  const [isDurationDialogOpen, setIsDurationDialogOpen] = useState(false);
  const [isGroupSizeDialogOpen, setIsGroupSizeDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedGroupSize, setSelectedGroupSize] = useState(null);
  
  // Form states
  const [workshopForm, setWorkshopForm] = useState({
    title: '',
    slug: '',
    category: 'general',
    short_description: '',
    full_description: '',
    target_audience: [],
    outcomes: [],
    icon: 'FileText',
    has_detail_page: true
  });
  
  const [durationForm, setDurationForm] = useState({
    id: '',
    label: '',
    minutes: 60,
    multiplier: 1.0
  });
  
  const [groupSizeForm, setGroupSizeForm] = useState({
    id: '',
    label: '',
    min: 1,
    max: 10,
    base_price: 150
  });
  
  const [outcomeInput, setOutcomeInput] = useState('');
  const [audienceInput, setAudienceInput] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [workshopsRes, pricingRes] = await Promise.all([
        api.get('/admin/services/workshops'),
        api.get('/admin/services/pricing-matrix')
      ]);
      setWorkshops(workshopsRes.data || []);
      // Map snake_case from API to camelCase expected by frontend
      const pricingData = pricingRes.data || {};
      setPricingMatrix({
        durations: pricingData.durations || [],
        groupSizes: pricingData.group_sizes || pricingData.groupSizes || []
      });
    } catch (error) {
      toast.error('Failed to load workshop data');
    } finally {
      setLoading(false);
    }
  };

  // Workshop handlers
  const openWorkshopDialog = (workshop = null) => {
    if (workshop) {
      setWorkshopForm({
        title: workshop.title || '',
        slug: workshop.slug || '',
        category: workshop.category || 'general',
        short_description: workshop.short_description || '',
        full_description: workshop.full_description || '',
        target_audience: workshop.target_audience || [],
        outcomes: workshop.outcomes || [],
        icon: workshop.icon || 'FileText',
        has_detail_page: workshop.has_detail_page !== false
      });
      setSelectedWorkshop(workshop);
    } else {
      setWorkshopForm({
        title: '',
        slug: '',
        category: 'general',
        short_description: '',
        full_description: '',
        target_audience: [],
        outcomes: [],
        icon: 'FileText',
        has_detail_page: true
      });
      setSelectedWorkshop(null);
    }
    setIsWorkshopDialogOpen(true);
  };

  const saveWorkshop = async () => {
    if (!workshopForm.title) {
      toast.error('Title is required');
      return;
    }
    
    try {
      if (selectedWorkshop) {
        await api.put(`/admin/services/workshops/${selectedWorkshop.id}`, workshopForm);
        toast.success('Workshop updated');
      } else {
        await api.post('/admin/services/workshops', workshopForm);
        toast.success('Workshop created');
      }
      setIsWorkshopDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save workshop');
    }
  };

  const deleteWorkshop = async () => {
    if (!deleteTarget) return;
    
    try {
      await api.delete(`/admin/services/workshops/${deleteTarget.id}`);
      toast.success('Workshop deleted');
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete workshop');
    }
  };

  const addOutcome = () => {
    if (outcomeInput.trim()) {
      setWorkshopForm({
        ...workshopForm,
        outcomes: [...workshopForm.outcomes, outcomeInput.trim()]
      });
      setOutcomeInput('');
    }
  };

  const removeOutcome = (index) => {
    setWorkshopForm({
      ...workshopForm,
      outcomes: workshopForm.outcomes.filter((_, i) => i !== index)
    });
  };

  const addAudience = () => {
    if (audienceInput.trim()) {
      setWorkshopForm({
        ...workshopForm,
        target_audience: [...workshopForm.target_audience, audienceInput.trim()]
      });
      setAudienceInput('');
    }
  };

  const removeAudience = (index) => {
    setWorkshopForm({
      ...workshopForm,
      target_audience: workshopForm.target_audience.filter((_, i) => i !== index)
    });
  };

  // Pricing handlers
  const openDurationDialog = (duration = null) => {
    if (duration) {
      setDurationForm({
        id: duration.id,
        label: duration.label,
        minutes: duration.minutes,
        multiplier: duration.multiplier
      });
      setSelectedDuration(duration);
    } else {
      setDurationForm({ id: '', label: '', minutes: 60, multiplier: 1.0 });
      setSelectedDuration(null);
    }
    setIsDurationDialogOpen(true);
  };

  const saveDuration = async () => {
    if (!durationForm.label) {
      toast.error('Label is required');
      return;
    }
    
    const newDurations = selectedDuration
      ? pricingMatrix.durations.map(d => d.id === selectedDuration.id ? { ...durationForm, id: selectedDuration.id } : d)
      : [...pricingMatrix.durations, { ...durationForm, id: durationForm.id || durationForm.label.toLowerCase().replace(/\s+/g, '-') }];
    
    try {
      // Send group_sizes (snake_case) to API
      await api.put('/admin/services/pricing-matrix', {
        durations: newDurations,
        group_sizes: pricingMatrix.groupSizes
      });
      toast.success(selectedDuration ? 'Duration updated' : 'Duration added');
      setIsDurationDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save duration');
    }
  };

  const deleteDuration = async (duration) => {
    try {
      const newDurations = pricingMatrix.durations.filter(d => d.id !== duration.id);
      // Send group_sizes (snake_case) to API
      await api.put('/admin/services/pricing-matrix', {
        durations: newDurations,
        group_sizes: pricingMatrix.groupSizes
      });
      toast.success('Duration deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete duration');
    }
  };

  const openGroupSizeDialog = (groupSize = null) => {
    if (groupSize) {
      setGroupSizeForm({
        id: groupSize.id,
        label: groupSize.label,
        min: groupSize.min,
        max: groupSize.max,
        base_price: groupSize.base_price
      });
      setSelectedGroupSize(groupSize);
    } else {
      setGroupSizeForm({ id: '', label: '', min: 1, max: 10, base_price: 150 });
      setSelectedGroupSize(null);
    }
    setIsGroupSizeDialogOpen(true);
  };

  const saveGroupSize = async () => {
    if (!groupSizeForm.label) {
      toast.error('Label is required');
      return;
    }
    
    const newGroupSizes = selectedGroupSize
      ? pricingMatrix.groupSizes.map(g => g.id === selectedGroupSize.id ? { ...groupSizeForm, id: selectedGroupSize.id } : g)
      : [...pricingMatrix.groupSizes, { ...groupSizeForm, id: groupSizeForm.id || groupSizeForm.label.toLowerCase().replace(/\s+/g, '-') }];
    
    try {
      // Send group_sizes (snake_case) to API
      await api.put('/admin/services/pricing-matrix', {
        durations: pricingMatrix.durations,
        group_sizes: newGroupSizes
      });
      toast.success(selectedGroupSize ? 'Group size updated' : 'Group size added');
      setIsGroupSizeDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save group size');
    }
  };

  const deleteGroupSize = async (groupSize) => {
    try {
      const newGroupSizes = pricingMatrix.groupSizes.filter(g => g.id !== groupSize.id);
      // Send group_sizes (snake_case) to API
      await api.put('/admin/services/pricing-matrix', {
        durations: pricingMatrix.durations,
        group_sizes: newGroupSizes
      });
      toast.success('Group size deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete group size');
    }
  };

  const filteredWorkshops = workshops.filter(w =>
    w.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-workshops-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6" />
            Workshop Management
          </h1>
          <p className="text-muted-foreground">Manage workshop topics and pricing</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="topics">Workshop Topics</TabsTrigger>
          <TabsTrigger value="pricing">Pricing Matrix</TabsTrigger>
        </TabsList>

        {/* Workshop Topics Tab */}
        <TabsContent value="topics" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Workshop Topics ({workshops.length})</CardTitle>
                <Button onClick={() => openWorkshopDialog()} data-testid="add-workshop-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Workshop
                </Button>
              </div>
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search workshops..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Outcomes</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWorkshops.map((workshop) => (
                    <TableRow key={workshop.id}>
                      <TableCell>
                        <div>
                          <span className="font-medium">{workshop.title}</span>
                          <span className="block text-sm text-muted-foreground">{workshop.slug}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {categoryOptions.find(c => c.id === workshop.category)?.label || workshop.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {workshop.outcomes?.length || 0} outcomes
                      </TableCell>
                      <TableCell>
                        <Badge variant={workshop.is_active ? 'default' : 'secondary'}>
                          {workshop.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openWorkshopDialog(workshop)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-destructive"
                          onClick={() => { setDeleteTarget(workshop); setIsDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredWorkshops.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No workshops found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Matrix Tab */}
        <TabsContent value="pricing" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Durations */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Workshop Durations
                  </CardTitle>
                  <Button size="sm" onClick={() => openDurationDialog()}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Label</TableHead>
                      <TableHead>Minutes</TableHead>
                      <TableHead>Multiplier</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pricingMatrix.durations?.map((duration) => (
                      <TableRow key={duration.id}>
                        <TableCell className="font-medium">{duration.label}</TableCell>
                        <TableCell>{duration.minutes} min</TableCell>
                        <TableCell>{duration.multiplier}x</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openDurationDialog(duration)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteDuration(duration)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Group Sizes */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Group Sizes
                  </CardTitle>
                  <Button size="sm" onClick={() => openGroupSizeDialog()}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Label</TableHead>
                      <TableHead>Range</TableHead>
                      <TableHead>Base Price</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pricingMatrix.groupSizes?.map((groupSize) => (
                      <TableRow key={groupSize.id}>
                        <TableCell className="font-medium">{groupSize.label}</TableCell>
                        <TableCell>{groupSize.min} - {groupSize.max}</TableCell>
                        <TableCell>${groupSize.base_price}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openGroupSizeDialog(groupSize)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteGroupSize(groupSize)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Pricing Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Pricing Preview</CardTitle>
              <CardDescription>Calculated prices based on duration × group size</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border p-3 text-left">Duration</th>
                      {pricingMatrix.groupSizes?.map(g => (
                        <th key={g.id} className="border p-3 text-center">{g.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pricingMatrix.durations?.map(d => (
                      <tr key={d.id}>
                        <td className="border p-3 font-medium">{d.label}</td>
                        {pricingMatrix.groupSizes?.map(g => (
                          <td key={g.id} className="border p-3 text-center">
                            ${Math.round(g.base_price * d.multiplier)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Workshop Dialog */}
      <Dialog open={isWorkshopDialogOpen} onOpenChange={setIsWorkshopDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedWorkshop ? 'Edit Workshop' : 'Add Workshop'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={workshopForm.title}
                  onChange={(e) => setWorkshopForm({ ...workshopForm, title: e.target.value })}
                  placeholder="Workshop title"
                />
              </div>
              <div className="space-y-2">
                <Label>Slug</Label>
                <Input
                  value={workshopForm.slug}
                  onChange={(e) => setWorkshopForm({ ...workshopForm, slug: e.target.value })}
                  placeholder="Auto-generated if empty"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={workshopForm.category}
                  onValueChange={(v) => setWorkshopForm({ ...workshopForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select
                  value={workshopForm.icon}
                  onValueChange={(v) => setWorkshopForm({ ...workshopForm, icon: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map(icon => (
                      <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Short Description</Label>
              <Textarea
                value={workshopForm.short_description}
                onChange={(e) => setWorkshopForm({ ...workshopForm, short_description: e.target.value })}
                placeholder="Brief workshop description"
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Full Description</Label>
              <Textarea
                value={workshopForm.full_description}
                onChange={(e) => setWorkshopForm({ ...workshopForm, full_description: e.target.value })}
                placeholder="Detailed workshop description"
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Learning Outcomes</Label>
              <div className="flex gap-2">
                <Input
                  value={outcomeInput}
                  onChange={(e) => setOutcomeInput(e.target.value)}
                  placeholder="Add an outcome"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOutcome())}
                />
                <Button type="button" onClick={addOutcome}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {workshopForm.outcomes.map((outcome, idx) => (
                  <Badge key={idx} variant="secondary" className="cursor-pointer" onClick={() => removeOutcome(idx)}>
                    {outcome} ×
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Target Audience</Label>
              <div className="flex gap-2">
                <Input
                  value={audienceInput}
                  onChange={(e) => setAudienceInput(e.target.value)}
                  placeholder="Add target audience"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAudience())}
                />
                <Button type="button" onClick={addAudience}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {workshopForm.target_audience.map((audience, idx) => (
                  <Badge key={idx} variant="outline" className="cursor-pointer" onClick={() => removeAudience(idx)}>
                    {audience} ×
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={workshopForm.has_detail_page}
                onCheckedChange={(checked) => setWorkshopForm({ ...workshopForm, has_detail_page: checked })}
              />
              <Label>Has detail page</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsWorkshopDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveWorkshop}>
              <Save className="w-4 h-4 mr-2" />
              Save Workshop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duration Dialog */}
      <Dialog open={isDurationDialogOpen} onOpenChange={setIsDurationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedDuration ? 'Edit Duration' : 'Add Duration'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input
                value={durationForm.label}
                onChange={(e) => setDurationForm({ ...durationForm, label: e.target.value })}
                placeholder="e.g., Standard Workshop"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Minutes</Label>
                <Input
                  type="number"
                  value={durationForm.minutes}
                  onChange={(e) => setDurationForm({ ...durationForm, minutes: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Price Multiplier</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={durationForm.multiplier}
                  onChange={(e) => setDurationForm({ ...durationForm, multiplier: parseFloat(e.target.value) || 1 })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDurationDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveDuration}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Size Dialog */}
      <Dialog open={isGroupSizeDialogOpen} onOpenChange={setIsGroupSizeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedGroupSize ? 'Edit Group Size' : 'Add Group Size'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input
                value={groupSizeForm.label}
                onChange={(e) => setGroupSizeForm({ ...groupSizeForm, label: e.target.value })}
                placeholder="e.g., Small Group (1-5)"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Min</Label>
                <Input
                  type="number"
                  value={groupSizeForm.min}
                  onChange={(e) => setGroupSizeForm({ ...groupSizeForm, min: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max</Label>
                <Input
                  type="number"
                  value={groupSizeForm.max}
                  onChange={(e) => setGroupSizeForm({ ...groupSizeForm, max: parseInt(e.target.value) || 10 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Base Price ($)</Label>
                <Input
                  type="number"
                  value={groupSizeForm.base_price}
                  onChange={(e) => setGroupSizeForm({ ...groupSizeForm, base_price: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGroupSizeDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveGroupSize}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workshop?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTarget?.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteWorkshop} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminWorkshops;
